import { useState } from "react";
import {
    ResponsiveContainer,
    PieChart,
    Pie,
    Cell,
    LineChart,
    Line,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
} from "recharts";
import { Layers, MapPin, Building, Trophy, AlertTriangle, Boxes } from "lucide-react";

import { useDashboard } from "@/lib/dataContext";
import { PageHeader } from "@/components/common/PageHeader";
import { ChartCard } from "@/components/common/ChartCard";
import { StatusBadge } from "@/components/common/StatusBadge";
import { KpiCard } from "@/components/common/KpiCard";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { ChartTooltip, axisProps, gridProps } from "@/components/common/ChartTooltip";
import { fmt, fmtDecimal, fmtPct, WINGS, wingColor, chartColor, youthPct } from "@/lib/format";

const DIMENSIONS = [
    { key: "by_instances", label: "By Instances", metric: "reported_instances", axis: "Instances" },
    { key: "by_total_attendance", label: "By Total Attendance", metric: "total_attendance", axis: "Attendance" },
    { key: "by_average_attendance", label: "By Avg Attendance", metric: "average_attendance_per_instance", axis: "Avg" },
];
const CORE_TABS = [
    { code: "spiritual", label: "Spiritual" },
    { code: "education", label: "Education" },
    { code: "service", label: "Service" },
];

export default function Activities() {
    const { data } = useDashboard();
    const annual = data.full_year_activity_summary;
    const wd = annual.wing_details || {};
    const idx = annual.analytical_indices;
    const coverage = data.monthly_reporting_coverage;
    const groups = data.activity_group_summary;
    const topByWing = data.top_activities_by_wing;
    const gapDistricts = (data.district_reporting_gap_rankings?.no_activity_at_all || []).slice(0, 8);

    const [wing, setWing] = useState("spiritual");
    const [dim, setDim] = useState("by_instances");
    const dimension = DIMENSIONS.find((d) => d.key === dim);
    const topList = (topByWing?.[wing]?.[dim]) || [];

    const totalWingActs = WINGS.reduce((s, w) => s + (wd[w.code]?.reported_activities || 0), 0) || 1;
    const donutData = WINGS.map((w) => ({
        name: w.name,
        value: wd[w.code]?.reported_activities || 0,
        pct: wd[w.code]?.activity_share_pct ?? ((wd[w.code]?.reported_activities || 0) / totalWingActs) * 100,
        color: wingColor(w.code),
    })).filter((d) => d.value > 0);

    const level = annual.by_conducting_level || {};
    const totalLevel = (level.samithi || 0) + (level.district || 0) + (level.state || 0) + (level.national || 0) || 1;

    const coverageData = coverage.map((c) => ({
        month: c.month,
        "No activity": c.no_activity_at_all.share_pct,
        "No spiritual": c.no_spiritual_activity.share_pct,
        "No service": c.no_service_activity.share_pct,
    }));

    const q1 = coverage[0];
    const q4 = coverage[coverage.length - 1];

    const indices = [
        { label: "Wing Balance Index", ...idx.wing_balance_index, hint: "Spread across Spiritual, Education & Service" },
        { label: "Activity Diversity Score", ...idx.programme_variety_score, hint: `${idx.programme_variety_score.distinct_activity_names} distinct activities` },
        { label: "Activity Concentration", ...idx.programme_concentration_index, hint: "By activity name" },
        { label: "Attendance Concentration", ...idx.attendance_concentration_index, hint: "By attendance share" },
    ];

    const barData = topList.map((a) => ({
        name: a.activity_name.length > 22 ? a.activity_name.slice(0, 21) + "…" : a.activity_name,
        value: a[dimension.metric],
    }));

    return (
        <div className="space-y-8">
            <PageHeader
                eyebrow="Activities & Wings"
                title="Activities across the wings"
                description="How activity is distributed across the organisation's service and spiritual wings."
            />

            {/* Core vs Ancillary */}
            <div>
                <div className="mb-4 flex items-center gap-2">
                    <Boxes className="h-5 w-5 text-primary" />
                    <h2 className="font-display text-xl font-semibold text-foreground">Core and Ancillary Activities</h2>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {groups.map((g, i) => (
                        <Card key={g.group_code} className={`border-l-4 border-border/70 bg-card p-5 shadow-card ${g.group_code === "core" ? "border-l-chart-3" : "border-l-chart-4"}`}>
                            <div className="flex items-start justify-between">
                                <div>
                                    <p className="font-semibold text-foreground">{g.group_name}</p>
                                    <p className="mt-0.5 text-xs capitalize text-muted-foreground">{g.included_wings.join(", ").replace(/_/g, " ")}</p>
                                </div>
                                <span className="font-display text-2xl font-semibold tabular text-foreground">{fmtPct(g.activity_share_pct)}</span>
                            </div>
                            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
                                {[
                                    { k: "Activities", v: fmt(g.reported_activities) },
                                    { k: "Attendance", v: fmt(g.total_attendance) },
                                    { k: "Avg", v: fmtDecimal(g.average_attendance_per_activity) },
                                    { k: "Youth %", v: fmtPct(g.youth_participation_pct) },
                                ].map((s) => (
                                    <div key={s.k} className="rounded-lg border border-border/60 bg-secondary/40 p-2.5">
                                        <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{s.k}</p>
                                        <p className="mt-0.5 text-base font-semibold tabular text-foreground">{s.v}</p>
                                    </div>
                                ))}
                            </div>
                            <div className="mt-3 flex items-center gap-3 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: chartColor(2) }} /> Gents {fmtPct(g.gender_engagement.gents_share_pct, 0)}</span>
                                <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: chartColor(4) }} /> Ladies {fmtPct(g.gender_engagement.ladies_share_pct, 0)}</span>
                                {g.beneficiaries > 0 && <span className="ml-auto">Beneficiaries {fmt(g.beneficiaries)}</span>}
                            </div>
                        </Card>
                    ))}
                </div>
            </div>

            {/* Wing mix + performance */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
                <ChartCard className="lg:col-span-2" title="Activity Share" description="By wing, full year">
                    <div className="h-[220px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={donutData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={52} outerRadius={82} paddingAngle={2} stroke="hsl(var(--card))" strokeWidth={2}>
                                    {donutData.map((d, i) => <Cell key={i} fill={d.color} />)}
                                </Pie>
                                <Tooltip content={<ChartTooltip formatter={(v, e) => `${fmt(v)} (${fmtPct(e.payload.pct)})`} />} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="mt-3 space-y-1.5">
                        {donutData.map((d) => (
                            <div key={d.name} className="flex items-center gap-2 text-xs">
                                <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: d.color }} />
                                <span className="truncate text-muted-foreground">{d.name}</span>
                                <span className="ml-auto font-semibold tabular text-foreground">{fmt(d.value)}</span>
                                <span className="w-11 text-right tabular text-muted-foreground">{fmtPct(d.pct)}</span>
                            </div>
                        ))}
                    </div>
                </ChartCard>

                <ChartCard className="lg:col-span-3" title="Wing Performance" description="Attendance and engagement per wing">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="border-border/70">
                                    <TableHead>Wing</TableHead>
                                    <TableHead className="text-right">Activities</TableHead>
                                    <TableHead className="text-right">Share</TableHead>
                                    <TableHead className="text-right">Attendance</TableHead>
                                    <TableHead className="text-right">Avg</TableHead>
                                    <TableHead className="text-right">Youth %</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {WINGS.map((w) => {
                                    const d = wd[w.code] || {};
                                    return (
                                        <TableRow key={w.code} className="border-border/60">
                                            <TableCell>
                                                <span className="flex items-center gap-2 font-medium text-foreground">
                                                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: wingColor(w.code) }} />
                                                    {w.name}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right tabular">{fmt(d.reported_activities || 0)}</TableCell>
                                            <TableCell className="text-right tabular">{fmtPct(d.activity_share_pct || 0)}</TableCell>
                                            <TableCell className="text-right tabular">{fmt(d.total_attendance || 0)}</TableCell>
                                            <TableCell className="text-right tabular">{fmtDecimal(d.average_attendance_per_activity || 0)}</TableCell>
                                            <TableCell className="text-right tabular">{(d.total_attendance || 0) > 0 ? fmtPct(youthPct(d)) : "—"}</TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </ChartCard>
            </div>

            {/* Top activities explorer */}
            <ChartCard
                title="Top Activities by Wing"
                description="Leading core-wing activities, ranked by the selected dimension"
                action={<span className="hidden items-center gap-1.5 text-xs text-muted-foreground sm:flex"><Trophy className="h-4 w-4" /> Ranked</span>}
            >
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <Tabs value={wing} onValueChange={setWing}>
                        <TabsList>
                            {CORE_TABS.map((t) => (
                                <TabsTrigger key={t.code} value={t.code} className="text-xs">{t.label}</TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                    <Tabs value={dim} onValueChange={setDim}>
                        <TabsList>
                            {DIMENSIONS.map((d) => (
                                <TabsTrigger key={d.key} value={d.key} className="text-xs">{d.label}</TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>

                {topList.length === 0 ? (
                    <p className="mt-6 text-sm text-muted-foreground">No ranked activities available for this wing.</p>
                ) : (
                    <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-5">
                        <div className="lg:col-span-2">
                            <div className="h-[240px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={barData} layout="vertical" margin={{ top: 0, right: 24, left: 8, bottom: 0 }}>
                                        <CartesianGrid {...gridProps} horizontal={false} vertical />
                                        <XAxis type="number" {...axisProps} />
                                        <YAxis type="category" dataKey="name" {...axisProps} width={120} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }} />
                                        <Tooltip content={<ChartTooltip formatter={(v) => fmt(v)} />} cursor={{ fill: "hsl(var(--muted))", opacity: 0.4 }} />
                                        <Bar dataKey="value" name={dimension.axis} fill={wingColor(wing)} radius={[0, 6, 6, 0]} maxBarSize={22} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                        <div className="lg:col-span-3">
                            <div className="overflow-x-auto rounded-lg border border-border/60">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-border/70 hover:bg-transparent">
                                            <TableHead>Activity</TableHead>
                                            <TableHead className="text-right">Instances</TableHead>
                                            <TableHead className="text-right">Attendance</TableHead>
                                            <TableHead className="text-right">Avg</TableHead>
                                            <TableHead className="text-right">Youth %</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {topList.map((a) => (
                                            <TableRow key={a.activity_id} className="border-border/50">
                                                <TableCell className="font-medium text-foreground">{a.activity_name}</TableCell>
                                                <TableCell className="text-right tabular">{fmt(a.reported_instances)}</TableCell>
                                                <TableCell className="text-right tabular">{fmt(a.total_attendance)}</TableCell>
                                                <TableCell className="text-right tabular">{fmtDecimal(a.average_attendance_per_instance)}</TableCell>
                                                <TableCell className="text-right tabular">{fmtPct(a.youth_participation_pct)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    </div>
                )}
            </ChartCard>

            {/* Conducting level + indices */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                <Card className="border-border/70 bg-card p-5 shadow-card">
                    <div className="flex items-center gap-2">
                        <Layers className="h-5 w-5 text-primary" />
                        <h3 className="font-semibold text-foreground">Where activities happen</h3>
                    </div>
                    <div className="mt-5 space-y-5">
                        <div>
                            <div className="mb-1.5 flex items-center justify-between text-sm">
                                <span className="flex items-center gap-2 text-muted-foreground"><Building className="h-4 w-4" /> Samithi level</span>
                                <span className="font-semibold tabular text-foreground">{fmt(level.samithi || 0)}</span>
                            </div>
                            <Progress value={((level.samithi || 0) / totalLevel) * 100} className="h-2" />
                        </div>
                        <div>
                            <div className="mb-1.5 flex items-center justify-between text-sm">
                                <span className="flex items-center gap-2 text-muted-foreground"><MapPin className="h-4 w-4" /> District level</span>
                                <span className="font-semibold tabular text-foreground">{fmt(level.district || 0)}</span>
                            </div>
                            <Progress value={((level.district || 0) / totalLevel) * 100} className="h-2" />
                        </div>
                        <p className="text-xs leading-relaxed text-muted-foreground">
                            {fmtPct(((level.samithi || 0) / totalLevel) * 100)} of all activities are conducted at the grassroots samithi level.
                        </p>
                    </div>
                </Card>

                <Card className="border-border/70 bg-card p-5 shadow-card lg:col-span-2">
                    <h3 className="font-semibold text-foreground">Activity Balance Indices</h3>
                    <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                        {indices.map((ix) => (
                            <div key={ix.label} className="rounded-xl border border-border/60 bg-secondary/40 p-4">
                                <div className="flex items-start justify-between gap-2">
                                    <p className="text-sm font-medium text-foreground">{ix.label}</p>
                                    <span className="font-display text-lg font-semibold tabular text-foreground">{fmtDecimal(ix.value, 2)}</span>
                                </div>
                                <div className="mt-2 flex items-center justify-between gap-2">
                                    <StatusBadge status={ix.status} />
                                    <span className="text-xs text-muted-foreground">{ix.hint}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>

            {/* Reporting coverage */}
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
                <ChartCard className="lg:col-span-3" title="Samithi Reporting Coverage" description="Share of the 247 samithis reporting no activity each month (lower is better)">
                    <div className="mb-4 grid grid-cols-3 gap-3">
                        <div className="rounded-lg border border-border/60 bg-secondary/40 p-3 text-center">
                            <p className="font-display text-2xl font-semibold tabular text-destructive">{fmt(q4.no_activity_at_all.count)}</p>
                            <p className="text-[11px] text-muted-foreground">No activity in Q4</p>
                        </div>
                        <div className="rounded-lg border border-border/60 bg-secondary/40 p-3 text-center">
                            <p className="font-display text-2xl font-semibold tabular text-foreground">{fmt(q4.no_education_activity.count)}</p>
                            <p className="text-[11px] text-muted-foreground">No education (Q4)</p>
                        </div>
                        <div className="rounded-lg border border-border/60 bg-secondary/40 p-3 text-center">
                            <p className="font-display text-2xl font-semibold tabular text-success">{fmtPct(q1.no_activity_at_all.share_pct - q4.no_activity_at_all.share_pct, 1)}</p>
                            <p className="text-[11px] text-muted-foreground">Q1→Q4 improvement</p>
                        </div>
                    </div>
                    <div className="h-[260px] w-full">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={coverageData} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                                <CartesianGrid {...gridProps} />
                                <XAxis dataKey="month" {...axisProps} />
                                <YAxis {...axisProps} unit="%" domain={[0, 100]} />
                                <Tooltip content={<ChartTooltip formatter={(v) => fmtPct(v)} />} />
                                <Legend iconType="circle" wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
                                <Line type="monotone" dataKey="No activity" stroke={wingColor("meetings_conferences")} strokeWidth={3} dot={{ r: 3 }} />
                                <Line type="monotone" dataKey="No spiritual" stroke={wingColor("spiritual")} strokeWidth={2.5} strokeDasharray="5 4" dot={{ r: 3 }} />
                                <Line type="monotone" dataKey="No service" stroke={wingColor("service")} strokeWidth={2.5} strokeDasharray="5 4" dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>

                <Card className="border-border/70 bg-card p-5 shadow-card lg:col-span-2">
                    <div className="mb-3 flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5 text-warning" />
                        <h3 className="font-semibold text-foreground">Largest reporting gaps</h3>
                    </div>
                    <p className="mb-3 text-xs text-muted-foreground">Districts with the highest share of samithis reporting no activity at all.</p>
                    <div className="space-y-2.5">
                        {gapDistricts.map((d) => (
                            <div key={d.district_id}>
                                <div className="mb-1 flex items-center justify-between text-sm">
                                    <span className="font-medium text-foreground">{d.district_name}</span>
                                    <span className="tabular text-muted-foreground">{d.gap_count}/{d.total_samithis} &middot; {fmtPct(d.gap_share_pct)}</span>
                                </div>
                                <Progress value={d.gap_share_pct} className="h-1.5" />
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
}
